import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { ModuleCommandStatusEnum } from "@/types/enums/command-status.enum";
import { useAreaDataProvider } from "../components/area-data-loader";

export const MODULE_RESPONSE_TIMEOUT_DELAY_SECONDS = 45;

type UseCommandTimeProgressionReturn = {
  isOutOfTimeChrono: boolean;
  progressPercent: number;
};

const computeProgress = (commandTimestampMs: number, now: DateTime) => {
  const elapsedSeconds = Math.max(
    0,
    (now.toMillis() - commandTimestampMs) / 1000
  );
  const progressPercent = Math.min(
    100,
    (elapsedSeconds / MODULE_RESPONSE_TIMEOUT_DELAY_SECONDS) * 100
  );
  return {
    progressPercent,
    isOutOfTimeChrono: progressPercent >= 100,
  };
};

// Statuts qui indiquent qu'une commande est "en vol" : soit on vient de la
// pousser localement en optimistic update (SENDING), soit le gateway l'a
// effectivement reçue (RECEIVED_BY_GATEWAY). Dans les deux cas, on chrono.
const IN_FLIGHT_STATUSES: ReadonlyArray<ModuleCommandStatusEnum> = [
  ModuleCommandStatusEnum.SENDING,
  ModuleCommandStatusEnum.RECEIVED_BY_GATEWAY,
];

const isCommandInFlight = (status: ModuleCommandStatusEnum) =>
  IN_FLIGHT_STATUSES.includes(status);

export function useCommandTimeProgression(): UseCommandTimeProgressionReturn {
  const { areaCommandTracking } = useAreaDataProvider();

  // On compare en ms pour éviter les pièges de comparaison de références
  // entre deux objets Date représentant le même instant.
  // NB: le typage backend annonce `Date`, mais en runtime c'est en fait une
  // string ISO (payload SSE désérialisé via JSON.parse). On normalise via
  // `new Date(...)` qui accepte les deux cas.
  const incomingTimestampMs = new Date(areaCommandTracking.timestamp).getTime();

  // Source de vérité du chrono : le timestamp de la commande qu'on a vue
  // passer en SENDING ou RECEIVED_BY_GATEWAY. Une fois capturé, le chrono
  // tourne jusqu'aux 45s, indépendamment des statuts/flags qui pourraient
  // bouger ensuite. Le seul reset, c'est l'arrivée d'une NOUVELLE commande
  // (timestamp différent) dans un de ces deux statuts.
  // Init synchrone au mount pour éviter un flash entre le premier render
  // et le passage du useEffect ci-dessous.
  const [trackedTimestampMs, setTrackedTimestampMs] = useState<number | null>(
    () =>
      isCommandInFlight(areaCommandTracking.workflowStatus)
        ? incomingTimestampMs
        : null
  );
  const [now, setNow] = useState(() => DateTime.local());

  useEffect(() => {
    const inFlight = isCommandInFlight(areaCommandTracking.workflowStatus);
    const isNewCommand = incomingTimestampMs !== trackedTimestampMs;

    if (inFlight && isNewCommand) {
      setTrackedTimestampMs(incomingTimestampMs);
      setNow(DateTime.local());
    }
  }, [
    areaCommandTracking.workflowStatus,
    incomingTimestampMs,
    trackedTimestampMs,
  ]);

  const { progressPercent, isOutOfTimeChrono } =
    trackedTimestampMs !== null
      ? computeProgress(trackedTimestampMs, now)
      : { progressPercent: 0, isOutOfTimeChrono: false };

  useEffect(() => {
    if (trackedTimestampMs === null || isOutOfTimeChrono) return;
    const interval = setInterval(() => setNow(DateTime.local()), 1000);
    return () => clearInterval(interval);
  }, [trackedTimestampMs, isOutOfTimeChrono]);

  return { isOutOfTimeChrono, progressPercent };
}
